/**
 * SEO Component - Gerencia meta tags e structured data
 * 
 * Features:
 * - Meta tags dinâmicas (title, description, keywords)
 * - Open Graph (Facebook, LinkedIn)
 * - Twitter Cards
 * - JSON-LD Structured Data
 * - Canonical URLs
 */

import { Helmet } from '@dr.pogodin/react-helmet'
import { FICHA_GOOGLE, linkFichaGoogle } from '@/lib/fichaGoogle'

export interface SEOProps {
  title?: string
  description?: string
  keywords?: string[]
  image?: string
  url?: string
  type?: 'website' | 'article' | 'profile' | 'product'
  author?: string
  publishedTime?: string
  modifiedTime?: string
  structuredData?: Record<string, any>
}

const DEFAULT_TITLE = 'TaMoAki - Peça um serviço a prestadores verificados em Luanda'
const DEFAULT_DESCRIPTION = 'Plataforma angolana para encontrar prestadores de serviços verificados. Conecte-se com profissionais qualificados em diversas áreas.'
const DEFAULT_IMAGE = 'https://tamoaki.co.ao/images/branding/og-image.png'
const DEFAULT_URL = 'https://tamoaki.co.ao'
const SITE_NAME = 'TaMoAki'

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = [],
  image = DEFAULT_IMAGE,
  url = DEFAULT_URL,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE
  const canonicalUrl = url.startsWith('http') ? url : `${DEFAULT_URL}${url}`

  // Structured Data padrão (Organization)
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: DEFAULT_URL,
    logo: DEFAULT_IMAGE,
    description: DEFAULT_DESCRIPTION,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+244-937-777-001',
      contactType: 'Customer Service',
      areaServed: 'AO',
      availableLanguage: 'pt',
    },
    sameAs: [
      'https://www.facebook.com/profile.php?id=61579116314098',
      'https://www.instagram.com/tamoaki.ao/',
      ...(linkFichaGoogle(FICHA_GOOGLE.perfil) ? [linkFichaGoogle(FICHA_GOOGLE.perfil) as string] : []),
    ],
  }

  const finalStructuredData = structuredData || defaultStructuredData

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      {author && <meta name="author" content={author} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="pt_AO" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="format-detection" content="telephone=no" />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">{JSON.stringify(finalStructuredData)}</script>
    </Helmet>
  )
}

